import { z } from "zod";
import { 
    NoneAuthSchema, 
    BearerAuthSchema, 
    HttpAuthSchema, 
    AwsAuthSchema, 
    ProxyAuthSchema, 
    PublicationType, 
    RendererType, 
    AllowedFormatsPro, 
    AllowedRenderersPro, 
    ReadAloudLanguage 
} from "../types";
import { AddonConfigSchema } from "../addons/schema";
import { AnyLayoutSchema } from "../layout";

export const ProV2Schema = z.object({
    plan: z.literal("pro"),
    version: z.literal("pro@2"),
    lastUpdated: z.string().datetime(),
    addons: AddonConfigSchema,
    layout: AnyLayoutSchema,
    private: z.object({
        origin: z.string().optional(),
        security: z.object({
            permittedUrls: z.array(z.string()).optional(),
            requirePreAuthorisation: z.boolean().optional()
        }).optional(),
        authentication: z.union([
            NoneAuthSchema,
            BearerAuthSchema,
            HttpAuthSchema,
            AwsAuthSchema,
            ProxyAuthSchema,
        ]),
    }),
    public: z.object({
        publication: z.object({
            key: z.string().optional()
        }).optional(),
        fonts: z.object({
            name: z.literal("components.publication_default")
        }).default({"name":"components.publication_default"}).optional(),
        settings: z.object({
            ignoreAspectRatio: z.object({
                enabled: z.boolean().default(false),
                value: z.boolean().default(false)
            }),
            fontSize: z.object({
                enabled: z.boolean().default(false),
                value: z.string().default('100%')
            }),
            lineHeight: z.object({
                enabled: z.boolean().default(false),
                value: z.string().default('100%')
            }),
            wordSpacing: z.object({
                enabled: z.boolean().default(false),
                value: z.string().default('components.publication_default')
            }),
            letterSpacing: z.object({
                enabled: z.boolean().default(false),
                value: z.string().default('components.publication_default')
            }),
            fontFamily: z.object({
                enabled: z.boolean().default(false),
                value: z.string().default('components.publication_default')
            }),
        }).partial().optional(),
        languages: z.array(z.string()).optional(),
        theme: z.union([
            z.string(), // Theme as string (e.g. "dark", "modern")
            z.object({
                accent: z.string(),
                isDark: z.boolean(),
                uiBackgroundDark: z.string(),
                uiBackgroundLight: z.string(),
                uiForegroundDark: z.string(),
                uiForegroundLight: z.string(),
                highlightColor: z.string(),
            }),
        ]),
        formats: z.array(PublicationType).default([...AllowedFormatsPro] as PublicationType[]),
        renderers: z.array(RendererType).default([...AllowedRenderersPro] as RendererType[]),
        readaloud: z.object({
            language: z.union([
                  ReadAloudLanguage,
                  z.string(),
                ]
            ).optional()
        }).optional()
    })
});

export type ProV2 = z.infer<typeof ProV2Schema>;